import React, { useState, useEffect } from 'react';
import {
    Button, Table, Card, Row, Col, Form, InputGroup,
    Spinner, Modal, Badge, Pagination
} from 'react-bootstrap';
import {
    collection, query, where, orderBy, getDocs, doc,
    deleteDoc, updateDoc, serverTimestamp, getFirestore
} from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import ProfesionForm from './ProfesionForm';

const ProfesionesScreen = ({ usuario }) => {
    // Estados para gestionar la pantalla
    const [profesiones, setProfesiones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentProfesion, setCurrentProfesion] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Acceder a Firestore
    const db = getFirestore(firebaseApp);

    // Obtener RUC y nombre de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';

    // Cargar profesiones al iniciar
    useEffect(() => {
        loadProfesiones();
    }, [ruc]);

    // Función para cargar profesiones de la empresa
    const loadProfesiones = async () => {
        try {
            setLoading(true);
            const profesionesRef = collection(db, 'rs_profesiones');
            const q = query(
                profesionesRef,
                where('ruc', '==', ruc),
                orderBy('nombre')
            );

            const snapshot = await getDocs(q);

            const profesionesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setProfesiones(profesionesList);
        } catch (error) {
            console.error("Error cargando profesiones:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar las profesiones. Por favor, inténtelo nuevamente.'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtrar profesiones según término de búsqueda
    const filteredProfesiones = profesiones.filter(profesion =>
        profesion.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (profesion.descripcion && profesion.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Calcular paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentProfesiones = filteredProfesiones.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProfesiones.length / itemsPerPage);

    // Cambiar de página
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Abrir modal para editar
    const handleEdit = (profesion) => {
        setCurrentProfesion(profesion);
        setShowModal(true);
    };

    // Abrir modal para crear nueva profesión
    const handleNew = () => {
        setCurrentProfesion(null);
        setShowModal(true);
    };

    // Eliminar una profesión
    const handleDelete = async (profesionId, nombreProfesion) => {
        try {
            const result = await Swal.fire({
                title: '¿Está seguro?',
                text: `Va a eliminar la profesión "${nombreProfesion}". Esta acción no se puede deshacer.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await deleteDoc(doc(db, 'rs_profesiones', profesionId));

                Swal.fire({
                    icon: 'success',
                    title: 'Eliminada',
                    text: 'La profesión ha sido eliminada correctamente.'
                });

                // Actualizar lista local sin necesidad de recargar de Firestore
                setProfesiones(prevProfesiones =>
                    prevProfesiones.filter(profesion => profesion.id !== profesionId)
                );
            }
        } catch (error) {
            console.error("Error eliminando profesión:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar la profesión. Es posible que esté siendo utilizada por perfiles o vacantes activas.'
            });
        }
    };

    // Guardar una profesión (nueva o edición)
    const handleSaveProfesion = async (formData) => {
        try {
            if (currentProfesion) {
                // Actualizar profesión existente
                await updateDoc(doc(db, 'rs_profesiones', currentProfesion.id), {
                    ...formData,
                    fechaActualizacion: serverTimestamp()
                });

                // Actualizar lista local
                setProfesiones(prevProfesiones =>
                    prevProfesiones.map(profesion =>
                        profesion.id === currentProfesion.id
                            ? { ...profesion, ...formData }
                            : profesion
                    )
                );

                Swal.fire({
                    icon: 'success',
                    title: 'Actualizada',
                    text: 'Profesión actualizada correctamente'
                });
            } else {
                // La creación se maneja en el componente ProfesionForm
                loadProfesiones(); // Recargar para obtener la profesión recién creada
                Swal.fire({
                    icon: 'success',
                    title: 'Creada',
                    text: 'Profesión creada correctamente'
                });
            }

            setShowModal(false);
        } catch (error) {
            console.error("Error guardando profesión:", error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al guardar la profesión.'
            });
        }
    };
    
    // Formatear nivel educativo para mostrar en la tabla
    const formatearNivelEducativo = (nivel) => {
        const niveles = {
            'secundaria': 'Secundaria Completa',
            'tecnica': 'Técnica',
            'universitaria': 'Universitaria',
            'postgrado': 'Postgrado/Maestría',
            'doctorado': 'Doctorado',
            'otro': 'Otro'
        };
        return niveles[nivel] || nivel;
    };

    return (
        <div className="p-1">
            <Row className="mb-4 align-items-center">
                <Col md={8}>
                    <h4 className="mb-0">Gestión de Profesiones y Carreras</h4>
                    <p className="text-muted mb-0">
                        Administre el catálogo de profesiones para los procesos de selección
                    </p>
                </Col>
                <Col md={4} className="text-end">
                    <Button 
                        variant="primary"
                        onClick={handleNew}
                        className="d-flex align-items-center ms-auto"
                    >
                        <FaPlus className="me-2" /> Nueva Profesión
                    </Button>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar por nombre o descripción..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1); // Resetear a primera página
                                    }}
                                />
                                {searchTerm && (
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={6} className="text-end text-muted">
                            {filteredProfesiones.length} profesiones encontradas
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Cargando profesiones...</p>
                        </div>
                    ) : currentProfesiones.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="mb-0 text-muted">
                                {searchTerm ? 'No se encontraron profesiones que coincidan con la búsqueda.' : 'No hay profesiones registradas.'}
                            </p>
                            {searchTerm && (
                                <Button 
                                    variant="link" 
                                    onClick={() => setSearchTerm('')}
                                >
                                    Limpiar búsqueda
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Nombre</th>
                                        <th>Nivel Educativo</th>
                                        <th>Especialidades</th>
                                        <th>Usos</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentProfesiones.map((profesion, index) => (
                                        <tr key={profesion.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>{profesion.nombre}</td>
                                            <td>
                                                <Badge bg="info" className="text-white">
                                                    {formatearNivelEducativo(profesion.nivelEducativo)}
                                                </Badge>
                                            </td>
                                            <td>
                                                {profesion.especialidades && profesion.especialidades.length > 0 ? (
                                                    <span>{profesion.especialidades.length} especialidades</span>
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <Badge bg={profesion.usos > 0 ? "success" : "secondary"}>
                                                    {profesion.usos || 0}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline-primary"
                                                        onClick={() => handleEdit(profesion)}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline-danger"
                                                        onClick={() => handleDelete(profesion.id, profesion.nombre)}
                                                        disabled={profesion.usos > 0}
                                                        title={profesion.usos > 0 ? "No se puede eliminar una profesión en uso" : ""}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
                                        
                                        {[...Array(totalPages)].map((_, i) => (
                                            <Pagination.Item 
                                                key={i + 1} 
                                                active={i + 1 === currentPage}
                                                onClick={() => paginate(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}
                                        
                                        <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal para crear/editar profesión */}
            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {currentProfesion ? 'Editar Profesión' : 'Nueva Profesión'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProfesionForm 
                        profesion={currentProfesion}
                        onSave={handleSaveProfesion}
                        onCancel={() => setShowModal(false)}
                        usuario={usuario}
                    />
                </Modal.Body>
            </Modal>

            {/* Tarjeta informativa */}
            <Card className="bg-light">
                <Card.Body>
                    <h5>Información</h5>
                    <ul className="mb-0">
                        <li>Registre las profesiones con sus respectivos niveles educativos</li>
                        <li>Puede agregar especialidades para cada profesión</li>
                        <li>Las profesiones en uso (con valor mayor a 0) no podrán ser eliminadas</li>
                        <li>Estas profesiones estarán disponibles al crear o editar vacantes</li>
                    </ul>
                </Card.Body>
            </Card>
        </div>
    );
};

export default ProfesionesScreen;